import { useEffect, useState } from "react";
import NotificationSettings from "./notifications";
import TeamSettings from "./team";
import { Supplier } from "../../models/supplier";
import SupplierSelector from "./supplier-selector";
import { ProfileSettings } from "./profile";
import { useNavigate } from "react-router-dom";
import { useAppDispatch } from "../../reducers/store";
import { getSupplierData } from "../../reducers/supplierSlice";
import { getProfiles, getFQMProfiles } from "../../reducers/profileSlice";
import { Person } from "../../models/person";

export function Settings() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [currentCard, setCurrentCard] = useState("profile");
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [profileSuppliers, setProfileSuppliers] = useState<Supplier[]>([]);
  const [selectedSuppliers, setSelectedSuppliers] = useState<Supplier[]>([]);
  const [selectedSingleSupplier, setSelectedSingleSupplier] =
    useState<Supplier>(null as any as Supplier);
  const [enableAllNotifications, setEnableAllNotifications] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [profilePicture, setProfilePicture] = useState("");
  const [isCurrentlySaving, setIsCurrentlySaving] = useState(false);

  useEffect(() => {
    const getSupplierInformation = async () => {
      try {
        const result = await dispatch(getSupplierData()) as {payload: Supplier[], type: string};
        const profiles = await dispatch(getProfiles()) as {payload: Person[], type: string};
        const fqmProfiles = await dispatch(getFQMProfiles()) as {payload: Person[], type: string};
        if (result.type.endsWith('/rejected')) {
          console.error('Failed to fetch supplier data');
          return;
        }
        if (profiles.type.endsWith('/rejected')) {
          console.error('Failed to fetch profiles');
          return;
        }
        if (fqmProfiles.type.endsWith('/rejected')) {
          console.error('Failed to fetch FQM profiles');
          return;
        }
        const profileSupplierIds = [...profiles.payload, ...fqmProfiles.payload].map(p => p.supplier);
        setProfileSuppliers(result.payload.filter(s => profileSupplierIds.includes(s.id)))
        setSuppliers(result.payload);
      } catch (error) {
        console.error('Error fetching supplier data:', error);
      }
    };

    setTimeout(() => {
      getSupplierInformation();
    }, 0);
  }, []);

  useEffect(() => {
    getProfilePicture();
  }, [currentCard]);

  useEffect(() => {

  }, [selectedSingleSupplier]);

  const onYourTeamClicked = () => {
    setCurrentCard("team");
  };
  const onNotificationsClicked = () => {
    setCurrentCard("notifications");
  };

  function getProfilePicture() {
    const pfp = localStorage.getItem("ProfilePicture");

    if (pfp) {
      setProfilePicture(pfp);
    } else {
      setProfilePicture("");
    }
  }

  const onProfileClicked = () => {
    setCurrentCard("profile");
  };
  const onLogOutClicked = () => {
    // Remove specific items from local storage
    localStorage.removeItem("auth_token");
    localStorage.removeItem("user_id");
    localStorage.removeItem("userEmail");
    localStorage.removeItem("selected_supplier");
    localStorage.removeItem("selected_producting_facilities");

    // Collect keys to remove
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith("qualityAlerts")) {
        keysToRemove.push(key);
      }
    }

    // Remove the collected keys
    keysToRemove.forEach((key) => localStorage.removeItem(key));

    // Reset state and navigate to login page
    navigate("/login");
  };
  const handleClick = (index: number) => {
    switch (index) {
      case 0:
        onProfileClicked();
        break;
      case 1:
        onNotificationsClicked();
        break;
      case 2:
        onYourTeamClicked();
        break;
      case 3:
        // Code for case 3
        break;
      default:
        // Code for default case
        break;
    }
    setActiveIndex(index);
  };

  const renderCard = () => {
    switch (currentCard) {
      case "profile":
        return (
          <div className="max-h-[60%]">
            {/* Profile card content */}
            <ProfileSettings pfp={profilePicture} setProfilePicture={setProfilePicture} />
          </div>
        );
      case "notifications":
        return (
          <div className="relative flex h-full w-full flex-col">
            {/* Notifications card content */}
            <div className="pb-[16px]">
              <span className="text-[20px] font-bold text-tqsBlack">
                Notifications
              </span>
            </div>

            <SupplierSelector
              currentCard={currentCard}
              suppliers={profileSuppliers}
              setSelectedSingleSupplier={setSelectedSingleSupplier}
              setSelectedSuppliers={setSelectedSuppliers}
              enableAllNotifications={enableAllNotifications}
              setEnableAllNotifications={setEnableAllNotifications}
              isCurrentlySaving={isCurrentlySaving}
            />
            <NotificationSettings
              selectedSupplier={selectedSingleSupplier}
              enableAllNotifications={enableAllNotifications}
              setEnableAllNotifications={setEnableAllNotifications}
              setSaveStatus={setIsCurrentlySaving}
            />
          </div>
        );
      case "team":
        return (
          <div className="relative flex h-full w-full flex-col">
            {/* Team card content */}
            <div className="pb-[16px]">
              <span className="text-[20px] font-bold text-tqsBlack">
                Your Team
              </span>
            </div>

            <SupplierSelector
              currentCard={currentCard}
              suppliers={suppliers}
              setSelectedSingleSupplier={setSelectedSingleSupplier}
              setSelectedSuppliers={setSelectedSuppliers}
              enableAllNotifications={enableAllNotifications}
              setEnableAllNotifications={setEnableAllNotifications}
              isCurrentlySaving={isCurrentlySaving}
            />

            <TeamSettings selectedSupplier={selectedSuppliers} />
          </div>
        );
      default:
        return null;
    }
  };
  return (
    <>
      <div className="flex h-[100%] w-[92%] pl-20 text-[black]">
        <div className="flex h-auto max-h-[400px] max-w-sm flex-col overflow-hidden rounded-lg bg-white px-2">
          <div className="px-6 py-4">
            <div className="mb-2 text-[22px] font-bold">
              Update and Manage your Account
            </div>
          </div>

          <div className="relative h-[65%] overflow-hidden px-6 pb-2 pt-2">
            {["Edit Profile", "Notifications", "Your Company Contacts"].map(
              (spanText, index) => (
                <div key={index} className="pb-3">
                  <span
                    className={`mb-2 mr-2 inline-block flex w-[100%] cursor-pointer items-center justify-start rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-700 ${
                      activeIndex === index ? "bg-yellow-100" : "bg-gray-200"
                    }`}
                    onClick={() => handleClick(index)}
                  >
                    {/* Add the appropriate SVG based on the index */}
                    <span className="mr-2 inline-block">
                      {index === 0 && (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          fill="currentColor"
                          className="mr-2 size-6 text-[#8e8e93]"
                        >
                          <path
                            fillRule="evenodd"
                            d="M7.5 6a4.5 4.5 0 1 1 9 0 4.5 4.5 0 0 1-9 0ZM3.751 20.105a8.25 8.25 0 0 1 16.498 0 .75.75 0 0 1-.437.695A18.683 18.683 0 0 1 12 22.5c-2.786 0-5.433-.608-7.812-1.7a.75.75 0 0 1-.437-.695Z"
                            clipRule="evenodd"
                          />
                        </svg>
                      )}
                      {index === 1 && (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          fill="currentColor"
                          className="mr-2 size-6 text-[#8e8e93]"
                        >
                          <path
                            fillRule="evenodd"
                            d="M5.25 9a6.75 6.75 0 0 1 13.5 0v.75c0 2.123.8 4.057 2.118 5.52a.75.75 0 0 1-.297 1.206c-1.544.57-3.16.99-4.831 1.243a3.75 3.75 0 1 1-7.48 0 24.585 24.585 0 0 1-4.831-1.244.75.75 0 0 1-.298-1.205A8.217 8.217 0 0 0 5.25 9.75V9Zm4.502 8.9a2.25 2.25 0 1 0 4.496 0 25.057 25.057 0 0 1-4.496 0Z"
                            clipRule="evenodd"
                          />
                        </svg>
                      )}
                      {index === 2 && (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          fill="currentColor"
                          className="mr-2 size-6 text-[#8e8e93]"
                        >
                          <path d="M4.5 6.375a4.125 4.125 0 1 1 8.25 0 4.125 4.125 0 0 1-8.25 0ZM14.25 8.625a3.375 3.375 0 1 1 6.75 0 3.375 3.375 0 0 1-6.75 0ZM1.5 19.125a7.125 7.125 0 0 1 14.25 0v.003l-.001.119a.75.75 0 0 1-.363.63 13.067 13.067 0 0 1-6.761 1.873c-2.472 0-4.786-.684-6.76-1.873a.75.75 0 0 1-.364-.63l-.001-.122ZM17.25 19.128l-.001.144a2.25 2.25 0 0 1-.233.96 10.088 10.088 0 0 0 5.06-1.01.75.75 0 0 0 .42-.643 4.875 4.875 0 0 0-6.957-4.611 8.586 8.586 0 0 1 1.71 5.157v.003Z" />
                        </svg>
                      )}
                    </span>
                    {spanText}
                  </span>
                </div>
              ),
            )}
          </div>
          <div className="mt-auto flex w-full px-6 pb-4">
            <span
              className="w-full cursor-pointer rounded-sm bg-gray-200 px-8 py-3 text-center text-sm font-semibold text-gray-700"
              onClick={onLogOutClicked}
            >
              Log out
            </span>
          </div>
        </div>
        <div className="relative flex h-[95%] w-[100%] flex-col rounded-md pl-16 max-w-[80%]">
          <div className="h-full bg-[white] p-[10px] rounded-lg overflow-auto">{renderCard()}</div>
        </div>
      </div>
    </>
  );
}
